export function getIndexRange(startIndex, endIndex) {
  const normalizedStart = Number.isInteger(startIndex) ? startIndex : 0
  const normalizedEnd = Number.isInteger(endIndex) ? endIndex : normalizedStart
  const from = Math.min(normalizedStart, normalizedEnd)
  const to = Math.max(normalizedStart, normalizedEnd)
  return Array.from({ length: to - from + 1 }, (_, index) => from + index)
}

export function getSelectionRange(anchorValue, targetValue, values = []) {
  if (!Array.isArray(values) || values.length === 0) {
    return [targetValue]
  }

  const normalizedAnchor = anchorValue ?? targetValue
  if (normalizedAnchor === null || normalizedAnchor === undefined) {
    return [targetValue]
  }

  const anchorIndex = values.indexOf(normalizedAnchor)
  const targetIndex = values.indexOf(targetValue)

  if (anchorIndex === -1 || targetIndex === -1) {
    return [targetValue]
  }

  const from = Math.min(anchorIndex, targetIndex)
  const to = Math.max(anchorIndex, targetIndex)
  return values.slice(from, to + 1)
}

export function getSelectionDragMetrics({
  selectedRects,
  fallbackRect,
  direction = 'vertical'
}) {
  const normalizedRects = (selectedRects || []).filter(
    (rect) => rect && typeof rect === 'object'
  )
  const fallback = fallbackRect || {}

  if (normalizedRects.length === 0) {
    return {
      width: fallback.width || 0,
      height: fallback.height || 0
    }
  }

  if (direction === 'horizontal') {
    return {
      width: normalizedRects.reduce((sum, rect) => sum + (rect.width || 0), 0),
      height: fallback.height || normalizedRects[0].height || 0
    }
  }

  return {
    width: fallback.width || normalizedRects[0].width || 0,
    height: normalizedRects.reduce((sum, rect) => sum + (rect.height || 0), 0)
  }
}

export function reorderSelection(items, selectedIndexes, fromIndex, toIndex) {
  if (!Array.isArray(items)) return items

  const validIndexes = (selectedIndexes || [])
    .map((index) => Number(index))
    .filter(
      (index) => Number.isInteger(index) && index >= 0 && index < items.length
    )
    .sort((a, b) => a - b)

  let fullSelection = []
  if (validIndexes.length > 0) {
    const minIndex = validIndexes[0]
    const maxIndex = validIndexes[validIndexes.length - 1]
    for (let i = minIndex; i <= maxIndex; i++) {
      fullSelection.push(i)
    }
  }

  const normalizedSelection = Array.from(new Set(fullSelection))

  if (normalizedSelection.length === 0) return [...items]

  const normalizedFrom = Number(fromIndex)
  const normalizedTo = Number(toIndex)

  if (
    !Number.isInteger(normalizedFrom) ||
    !Number.isInteger(normalizedTo) ||
    normalizedFrom < 0 ||
    normalizedTo < 0 ||
    normalizedFrom >= items.length ||
    normalizedTo > items.length
  ) {
    return [...items]
  }

  const selectedSet = new Set(normalizedSelection)
  const selectedItems = normalizedSelection.map((index) => items[index])
  const remainingItems = items.filter((_, index) => !selectedSet.has(index))

  const adjustedToIndex =
    normalizedFrom < normalizedTo
      ? Math.max(normalizedTo - normalizedSelection.length, 0)
      : normalizedTo

  const insertIndex = Math.min(adjustedToIndex, remainingItems.length)
  remainingItems.splice(insertIndex, 0, ...selectedItems)

  return remainingItems
}
